// @flow
import * as React from "react";
import { adopt } from "react-adopt";
import { withRouter } from "react-router";
import get from "lodash/get";

import { denormalizeJsonApi } from "buba-components/src/utils/denormalizeJsonApi";
import { FlashMessageConsumer } from "buba-components";
import DataFetcher from "buba-components/src/components/DataFetcher";
import InquiryPage from "buba-components/src/components/InquiryPage";
import statusErrors from "buba-components/src/data/statusErrors";
import { getAllFiles, getAndOpenBankOffer, downloadFile } from "../../api/actions";
import paths from "../../data/paths";
import { customerInquiryTabs } from "../../data/navLinks";
import CustomerInquiryDownloads from "./CustomerInquiryDownloads";
import CustomerInquiryUploads from "./CustomerInquiryUploads";

export type UploadedFile = {
  bankName: ?string,
  description: ?string,
  downloadedByBankAdvisorAt: ?string,
  downloadedByCustomerAt: ?string,
  downloadedByGuaranteeAgentAt: ?string,
  filename: string,
  hiddenFor: "customer" | "bank_advisor",
  id: string,
  uploadedBy: "guarantee_agent" | "customer" | "bank_advisor"
};

export type RequestedFile = {
  description: string,
  fileKey: string,
  id: string,
  requestedFrom: "customer" | "guarantee_agent"
};

export type RequestedFiles = RequestedFile[];
export type UploadedFiles = UploadedFile[];

type BankOfferParams = { inquiryId: number, render: () => React.Node };
type FileDataParams = {
  bankOffer: number,
  message: { addMessage: (msg: string, type: string) => void },
  render: () => React.Node
};
type CustomerInquiryDocumentsProps = {
  match: {
    params: { id: number }
  }
};

type SetBankInquiryDocumentsContents = {
  addMessage: (msg: string, type: string) => void,
  bankOffer: {
    data: { inquiryProcess: { id: number } }
  },
  bankOfferId: number,
  fileData: {
    data: {
      inquiryProcessUploadedFiles: UploadedFiles,
      fileRequests: RequestedFiles
    },
    requestFetch: any => Promise<any>
  }
};

const Composed = adopt({
  message: <FlashMessageConsumer />,
  bankOffer: ({ inquiryId, render }: BankOfferParams) => (
    <DataFetcher
      apiAction={getAndOpenBankOffer}
      mapper={denormalizeJsonApi}
      initialPayload={{ id: Number(inquiryId) }}
      redirectOnError
    >
      {render}
    </DataFetcher>
  ),
  fileData: ({ bankOffer, message: { addMessage }, render }: FileDataParams) => {
    const inquiryProcessId = get(bankOffer, "data.inquiryProcess.id");
    const bankOfferId = get(bankOffer, "data.id");
    if (!inquiryProcessId) {
      return null;
    }
    return (
      <DataFetcher
        apiAction={getAllFiles}
        mapper={([bankOfferFilesResponse, inquiryProcessFilesResponse, filesRequestsResponse]) => ({
          bankOfferUploadedFiles: denormalizeJsonApi(bankOfferFilesResponse),
          inquiryProcessUploadedFiles: denormalizeJsonApi(inquiryProcessFilesResponse),
          fileRequests: denormalizeJsonApi(filesRequestsResponse)
        })}
        onError={msg => {
          addMessage(msg, "failure");
        }}
        initialPayload={{
          inquiryProcessId: Number(inquiryProcessId),
          bankOfferId: Number(bankOfferId)
        }}
        redirectOnError
      >
        {render}
      </DataFetcher>
    );
  }
});

const setCustomerInquiryDocumentsContents = ({
  addMessage,
  bankOffer,
  fileData
}: SetBankInquiryDocumentsContents) => {
  const inquiryProcessId = get(bankOffer, "data.inquiryProcess.id");
  const inquiryProcessUploadedFiles = get(fileData, "data.inquiryProcessUploadedFiles", []);
  const bankOfferUploadedFiles = get(fileData, "data.bankOfferUploadedFiles", []);
  const inquiryProcessUploadedFilesByBankAdvisor = inquiryProcessUploadedFiles.filter(
    ({ uploadedBy }) => uploadedBy === "bank_advisor"
  );
  const fileRequests = get(fileData, "data.fileRequests", []);

  const documentsProps = {
    refetchFiles: fileData.requestFetch || null,
    onError: (statusCode: number) => addMessage(statusErrors(statusCode), "failure"),
    bankOfferId: Number(bankOffer.data.id),
    inquiryProcessId: Number(inquiryProcessId),
    downloadAction: downloadFile
  };
  return (
    <>
      <CustomerInquiryDownloads
        {...documentsProps}
        fileRequests={fileRequests}
        uploadedFiles={inquiryProcessUploadedFiles}
        addMessage={addMessage}
      />
      <CustomerInquiryUploads
        {...documentsProps}
        fileRequests={fileRequests}
        inquiryProcessUploadedFiles={inquiryProcessUploadedFilesByBankAdvisor}
        bankOfferUploadedFiles={bankOfferUploadedFiles}
      />
    </>
  );
};

const CustomerInquiryDocuments = ({
  match: {
    params: { id: inquiryId }
  }
}: CustomerInquiryDocumentsProps) => (
  <Composed inquiryId={inquiryId}>
    {({ bankOffer, fileData, message: { addMessage } }) => {
      const isLoading = bankOffer.isLoading || fileData.isLoading;

      return (
        <InquiryPage
          backPath={paths.customerInquiriesList}
          companyName={bankOffer.data ? bankOffer.data.company.name : ""}
          isLoadingData={isLoading}
          issuedOn={bankOffer.data ? new Date(bankOffer.data.createdAt) : null}
          tabs={customerInquiryTabs}
        >
          {setCustomerInquiryDocumentsContents({
            isLoading,
            addMessage,
            bankOffer,
            fileData
          })}
        </InquiryPage>
      );
    }}
  </Composed>
);

export default withRouter(CustomerInquiryDocuments);
